import moment from "moment";

// Helper function to extract standard code from description format
// e.g., "Responsible Wool Standard (RWS)" -> "RWS"
function extractStandardCode(standardDescription: string): string {
  const match = standardDescription.match(/\(([A-Z]+)\)$/);
  return match ? match[1] : standardDescription;
}

export interface ScopeCertificateDetails {
  scopeCertificateNo: string; // Numeric field
  brandName: string[]; // Multi-select dropdown
  gotsSCNo: string; // Optional text field
  scLegacyNo: string[]; // Optional text field
  scCBAuditorId: string[]; // Dropdown
  scStatus: string; // Dropdown
  scStatusDescription: string; // Description for scStatus dropdown
  scSuspensionReason: string; // Dropdown
  scSuspensionReasonDescription: string; // Description for scSuspensionReason dropdown
  scNumberOfFarms: number | null; // Number of farms - moved from StandardDetails
  scCBLicensingCode: string; // Text field
  scVersionNo: string; // Numeric field
  scProductLimitations: string; // New field from API
  scCreationTimestamp: string; // New field from API
  scCbLicensingCodeDescription: string; // New field from API
  ccsFlag: string; // Does CCS Apply field (radio - no description needed)
  stdClaimsPolicy:string;
  // Removed: scCertifiedOrganizationApprovedClaims - moved back to StandardDetails
}

export interface StandardDetails {
  scStandard: string; // Text field
  scStandardDescription?: string;
  scStandardVersion: string; // Text field
  scStandardAddedDate: string; // Date field - API field name
  scStandardRemovedDate: string; // Date field - API field name
  scStandardWithdrawalReason: string[]; // Multi-select dropdown field - API field name
  scStandardWithdrawalReasonDescription: string; // Description for scStandardWithdrawalReason dropdown
  certifiedOrganizationApprovedClaims: string; // Required field for API - moved back to StandardDetails
  // Removed: scNumberOfFarms - moved to ScopeCertificateDetails
  // Removed: numberOfAuditDays, certifiedOrganizationApprovedClaimsDescription
}

export interface ScopeCertificateDates {
  scCbAccreditationNo: string;
  scCreatedDate: string; // Date in 'YYYY-MM-DD' format (required)
  scLastUpdatedDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scExtendedUntilDate: string; // Date in 'YYYY-MM-DD' format (optional) - Updated to match API field name
  scLastAuditStartDate: string; // Date in 'YYYY-MM-DD' format (optional) - Updated to match API field name
  scValidFromDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scValidUntilDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scWithdrawalDate: string; // Date in 'YYYY-MM-DD' format (optional)
  scLastAuditDate: string; // Date in 'YYYY-MM-DD' format (optional)
  scStateOrProvince: string; // Dropdown (required)
  scStateOrProvinceDescription: string; // Description for scStateOrProvince dropdown
  scPlaceOfIssue: string; // StateName (CountryCode-StateCode) for API (required)
  scPlaceOfIssueDescription: string; // StateName (CountryCode-StateCode) for API (required)
  scCbAccreditationBody: string; // Dropdown (required) - API field name
  scCbAccreditationBodyDescription: string; // Description for scCbAccreditationBody dropdown
  scCbScopeStipulations: string; // Text field (optional) - API field name
}

export interface AccreditationDetails {
  scScopeStipulations: string; // Optional text field
  accreditationBody: string; // Dropdown
  accreditationBodyDescription: string; // Description for accreditationBody dropdown
  accreditationNo: string; // Optional text field
  inspectionBody: string; // Optional text field
}

export interface NonMandatoryCriterionDetails {
  scNonMandatoryCriterionStandard: string; // Dropdown field (required)
  scNonMandatoryCriterionStandardDescription: string; // Description for scNonMandatoryCriterionStandard dropdown
  scNonMandatoryCriterionVersion: string; // Text field (required)
  scNonMandatoryCriterionNumber: string; // Text field (required)
  scNonMandatoryCriterionStatus: string; // Dropdown field (required)
  scNonMandatoryCriterionStatusDescription: string; // Description for scNonMandatoryCriterionStatus dropdown
}

export interface ScopeCertificateForm {
  scopeCertificateDetails: ScopeCertificateDetails;
  standardDetails: StandardDetails[];
  scopeCertificateDates: ScopeCertificateDates;
  scNonMandatoryCriterionInformation: NonMandatoryCriterionDetails[];
  // Removed: accreditationDetails
}

export function mapToScopeCertificateForm(response: any): ScopeCertificateForm {
  // Parse place of issue to extract state code
  const placeOfIssue = response?.scPlaceOfIssue || "";
  
  const {
    scNo = '',
    scVersionNo = '',
    scPlaceOfIssueDescription = ''
  } = response || {};

  let state = "";
  
  // Check if it's the code format (XX-XX or XX-XXX): "IN-AS", "AU-VIC", etc. - as per documentation
  if (/^[A-Z]{2}-[A-Z]{2,3}$/.test(placeOfIssue)) {
    // New format: just the code - extract state part after hyphen (e.g., "AS" from "IN-AS")
    state = placeOfIssue.split('-')[1] || "";
  } else {
    // Legacy format: "Country (CC), StateName (CC-SC)" or "StateName (CC-SC)" - extract state code
    const statePart = placeOfIssue.includes(",")
      ? placeOfIssue.split(", ")[1]
      : placeOfIssue;
    const stateMatch = statePart.match(/\([A-Z]{2}-([A-Z0-9]+)\)/);
    state = stateMatch ? stateMatch[1] : "";
  }
  state = `${scPlaceOfIssueDescription} (${state})`
  return {
    scopeCertificateDetails: {
      scopeCertificateNo: scNo?scNo.substring(scNo.indexOf('-') + 1) : "", // Converting scId to number
      brandName: response.brandName || [], // Wrapping brandName in an array
      gotsSCNo: response.gotsScNo || "", // Optional field
      scLegacyNo: response.scLegacyNo || [], // Optional field
      scCBAuditorId: response.scCbAuditorId || [], // Taking the first auditor ID
      scStatus: response.scStatus || "", // Dropdown value
      scStatusDescription: response.scStatusDescription || "", // Description for scStatus dropdown
      scSuspensionReason: response.scSuspensionReason || "", // Dropdown value
      scSuspensionReasonDescription: response.scSuspensionReasonDescription || "", // Description for scSuspensionReason dropdown
      scNumberOfFarms: response.scNumberOfFarms || null, // Moved from StandardDetails
      scCBLicensingCode: response.scCbLicensingCode || "",
      scVersionNo: scVersionNo?scVersionNo.substring(scVersionNo.indexOf('-') + 1): "",
      scProductLimitations: response.scProductLimitations || "", // New field from API
      scCreationTimestamp: response.scCreationTimestamp || "", // New field from API
      scCbLicensingCodeDescription: response.scCbLicensingCodeDescription || "", // New field from API
      ccsFlag: response.ccsFlag || "", // Does CCS Apply field
      stdClaimsPolicy:response?.stdClaimsPolicy||""
      // Removed: scCertifiedOrganizationApprovedClaims - moved back to StandardDetails
    },
    standardDetails: response.scStandardInformation?.length > 0 ? response.scStandardInformation.map((item: any) => ({
      scStandard: item.scStandard,
      scStandardDescription: item.scStandardDescription,
      scStandardVersion: item.scStandardVersion,
      scStandardAddedDate: item.scStandardAddedDate,  // API field name
      scStandardRemovedDate: item.scStandardRemovedDate,  // API field name
      scStandardWithdrawalReason: Array.isArray(item.scStandardWithdrawalReason) 
        ? item.scStandardWithdrawalReason.map((reason: string) => {
            // Parse "code; description" format back to just the description for form display
            const parts = reason.split('; ');
            return parts.length > 1 ? parts[1] : reason;
          })
        : [],  // API field name - convert from ["code1; description1", "code2; description2"] to ["description1", "description2"]
      scStandardWithdrawalReasonDescription: item.scStandardWithdrawalReasonDescription || "", // Description for scStandardWithdrawalReason dropdown
      certifiedOrganizationApprovedClaims: item.certifiedOrganizationApprovedClaims || "", // Required field for API - moved back to StandardDetails
    })) : [],
    scopeCertificateDates: {
      scCbAccreditationNo: response.scCbAccreditationNo || "",
      scCreatedDate: response.scCreatedDate || "",
      scLastUpdatedDate: response.scLastUpdatedDate || "", // Updated to match API field name
      scExtendedUntilDate: response.scExtendedUntilDate || "", // Updated to match API field name
      scLastAuditStartDate: response.scLastAuditStartDate || "", // Updated to match API field name
      scValidFromDate: response.scValidFromDate || "", // Updated to match API field name
      scValidUntilDate: response.scValidUntilDate || "", // Updated to match API field name
      scWithdrawalDate: response.scWithdrawalDate || "",
      scLastAuditDate: response.scLastAuditDate || "",
      scStateOrProvince: state || "",
      scStateOrProvinceDescription: response.scStateOrProvinceDescription || "", // Description for scStateOrProvince dropdown
      scPlaceOfIssue: response.scPlaceOfIssue || "",
      scPlaceOfIssueDescription: response.scPlaceOfIssueDescription || "",
      scCbAccreditationBody: response.scCbAccreditationBody || "",
      scCbAccreditationBodyDescription: response.scCbAccreditationBodyDescription || "", // Description for scCbAccreditationBody dropdown
      scCbScopeStipulations: response.scCbScopeStipulations || "",
    },
    scNonMandatoryCriterionInformation: response.scNonMandatoryCriterionInformation?.length > 0 ? response.scNonMandatoryCriterionInformation.map((item: any) => ({
      scNonMandatoryCriterionStandard: item.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: item.scNonMandatoryCriterionStandardDescription || "", // Description for scNonMandatoryCriterionStandard dropdown
      scNonMandatoryCriterionVersion: item.scNonMandatoryCriterionVersion || "",
      scNonMandatoryCriterionNumber: item.scNonMandatoryCriterionNumber || "",
      scNonMandatoryCriterionStatus: item.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: item.scNonMandatoryCriterionStatusDescription || "", // Description for scNonMandatoryCriterionStatus dropdown
    })) : [],
  };
}

export function scopeCertificateFormToMap(scopeCertificateForm: ScopeCertificateForm, SC_ID?: string | undefined, cbLicenseCode?: any): any {
  const shortCode = cbLicenseCode?.replace(/^CB-/, "") || "";
  const { scopeCertificateDetails, standardDetails, scopeCertificateDates, scNonMandatoryCriterionInformation } = scopeCertificateForm;
  
  // Use the formatted place of issue directly
  const placeOfIssue = scopeCertificateDates.scPlaceOfIssue || "";
  const placeOfIssueDescription = scopeCertificateDates.scPlaceOfIssueDescription || "";
  
  const statusOptions: any = [
    { label: "Valid", value: "V" }, // Represents "Valid"
    { label: "Expired", value: "E" }, // Represents "Expired"
    { label: "Suspended", value: "S" }, // Represents "Suspended"
    { label: "Withdrawn", value: "W" }, // Represents "Withdrawn"
  ]
  const scStandardOptions: any = [
    { label: "Organic Content Standard", value: "OCS" }, // Represents "Valid"
    { label: "Global Recycled Standard", value: "GRS" }, // Represents "Expired" 
    { label: "Recycled Claim Standard", value: "RCS" }, // Represents "Suspended"
    { label: "Responsible Down Standard", value: "RDS" }, // Represents "Withdrawn"
    { label: "Responsible Wool Standard", value: "RWS" }, // Represents "Withdrawn"
    { label: "Responsible Mohair Standard", value: "RMS" }, // Represents "Withdrawn"
    { label: "Responsible Alpaca Standard", value: "RAS" }, // Represents "Withdrawn"
    { label: "Content Claim Standard", value: "CCS" }, // Represents "Withdrawn"
  ]


  return {
    scId: SC_ID,
    scNo: `${shortCode}-${scopeCertificateDetails.scopeCertificateNo}`,
    scVersionNo: `${shortCode}-${scopeCertificateDetails.scVersionNo.toString()}`, // Convert number to string
    gotsScNo: scopeCertificateDetails.gotsSCNo,
    brandName: scopeCertificateDetails.brandName,
    scCbAuditorId: scopeCertificateDetails.scCBAuditorId,
    scLegacyNo: scopeCertificateDetails.scLegacyNo,
    scStatus: scopeCertificateDetails.scStatus,
    scStatusDescription: scopeCertificateDetails.scStatusDescription,
    scSuspensionReason: scopeCertificateDetails.scSuspensionReason,
    scSuspensionReasonDescription: scopeCertificateDetails.scSuspensionReasonDescription,
    scNumberOfFarms: scopeCertificateDetails.scNumberOfFarms, // Moved from StandardDetails
    ccsFlag: scopeCertificateDetails.ccsFlag,
    stdClaimsPolicy:scopeCertificateDetails?.stdClaimsPolicy,
    scCbLicensingCode: cbLicenseCode,
    scCreatedDate: scopeCertificateDates.scCreatedDate || "",
    scLastUpdatedDate: scopeCertificateDates.scLastUpdatedDate || "", // Updated to match API field name
    scExtendedUntilDate: scopeCertificateDates.scExtendedUntilDate || "", // Updated to match API field name
    scLastAuditStartDate: scopeCertificateDates.scLastAuditStartDate || "", // Updated to match API field name
    scValidFromDate: scopeCertificateDates.scValidFromDate || "", // Updated to match API field name
    scValidUntilDate: scopeCertificateDates.scValidUntilDate || "", // Updated to match API field name
    scWithdrawalDate: scopeCertificateDates.scWithdrawalDate || "",
    scLastAuditDate: scopeCertificateDates.scLastAuditDate || "",
    scPlaceOfIssue: placeOfIssue,
    scPlaceOfIssueDescription: placeOfIssueDescription,
    scCbAccreditationBody: scopeCertificateDates.scCbAccreditationBody || "",
    scCbAccreditationBodyDescription: scopeCertificateDates.scCbAccreditationBodyDescription || "", // Description for scCbAccreditationBody dropdown
    scCbScopeStipulations: scopeCertificateDates.scCbScopeStipulations || "",
    scCbAccreditationNo: scopeCertificateDates.scCbAccreditationNo || "",
    scCreationTimestamp: scopeCertificateDetails.scCreationTimestamp || new Date().toISOString(), // Add missing field from API
    scProductLimitations: scopeCertificateDetails.scProductLimitations || "", // Add missing field from API
    // Removed: scCertifiedOrganizationApprovedClaims - moved back to StandardDetails
    scStandardInformation: standardDetails.length > 0 ? standardDetails.map((item: any) => {
      const standardCode = item.scStandard ? extractStandardCode(item.scStandard) : "";

      return {
        scStandard: standardCode,
        scStandardDescription: item.scStandardDescription || "",
        scStandardVersion: item.scStandardVersion || "",
        scStandardAddedDate: item.scStandardAddedDate || "",  // API field name
        scStandardRemovedDate: item.scStandardRemovedDate || "",  // API field name
        scStandardWithdrawalReason: Array.isArray(item.scStandardWithdrawalReason) 
          ? item.scStandardWithdrawalReason
          : [],  // API field name - withdrawal reason codes as-is
        scStandardWithdrawalReasonDescription: item.scStandardWithdrawalReasonDescription || "", // Description for scStandardWithdrawalReason dropdown
        certifiedOrganizationApprovedClaims: item.certifiedOrganizationApprovedClaims || "", // Required field for API - moved back to StandardDetails
      };
    }) : [],
    scNonMandatoryCriterionInformation: scNonMandatoryCriterionInformation.length > 0 ? scNonMandatoryCriterionInformation.map((item: any) => ({
      scNonMandatoryCriterionStandard: item.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: item.scNonMandatoryCriterionStandardDescription || "", // Description for scNonMandatoryCriterionStandard dropdown
      scNonMandatoryCriterionVersion: item.scNonMandatoryCriterionVersion || "",
      scNonMandatoryCriterionNumber: item.scNonMandatoryCriterionNumber || "",
      scNonMandatoryCriterionStatus: item.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: item.scNonMandatoryCriterionStatusDescription || "", // Description for scNonMandatoryCriterionStatus dropdown
    })) : [],
    scCbLicensingCodeDescription: scopeCertificateDetails.scCbLicensingCodeDescription || "", // Add missing field from API
  };
}
